if (!customElements.get('wallet-scroll-scrub')) {
  class WalletScrollScrub extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.stage = this.querySelector('[data-wallet-scroll-stage]');
      this.frameImage = this.querySelector('[data-wallet-scroll-frame]');
      this.backgroundCopyInner = this.querySelector('.wallet-scroll-scrub__background-copy-inner');
      this.captions = Array.from(this.querySelectorAll('[data-wallet-scroll-caption]')).sort(
        (first, second) => Number(first.dataset.captionOrder) - Number(second.dataset.captionOrder)
      );
      this.frameUrls = this.readFrameUrls();
      this.frameCache = [];
      this.currentFrameIndex = 0;
      this.updateScrollLayout = this.updateScrollLayout.bind(this);
      this.updateNativeProgress = this.updateNativeProgress.bind(this);
      this.handleNativeScroll = this.handleNativeScroll.bind(this);
      this.handleThemeEditorRefresh = this.handleThemeEditorRefresh.bind(this);

      document.addEventListener('shopify:section:load', this.handleThemeEditorRefresh);
      document.addEventListener('shopify:section:reorder', this.handleThemeEditorRefresh);

      this.updateContent(0);

      if (!this.stage || !this.frameImage || this.frameUrls.length === 0) return;

      this.prepareFrameLoad();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.scrollTrigger?.kill();
      this.removeNativeScrollFallback();
      this.observer = null;
      this.scrollTrigger = null;
      this.frameLoadStarted = false;
      this.framesReady = false;
      this.isInitialized = false;
      this.classList.remove('is-scroll-active', 'is-ready');
      document.removeEventListener('shopify:section:load', this.handleThemeEditorRefresh);
      document.removeEventListener('shopify:section:reorder', this.handleThemeEditorRefresh);
    }

    readFrameUrls() {
      const frameList = this.querySelector('[data-wallet-scroll-frame-list]');

      if (!frameList) return [];

      try {
        const urls = JSON.parse(frameList.textContent);
        return Array.isArray(urls) ? urls.filter(Boolean) : [];
      } catch (error) {
        return [];
      }
    }

    prepareFrameLoad() {
      if (!('IntersectionObserver' in window)) {
        this.loadFrames();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.observer.disconnect();
          this.loadFrames();
        },
        {
          rootMargin: '200% 0px',
          threshold: 0.01,
        }
      );

      this.observer.observe(this);
    }

    async loadFrames() {
      if (this.frameLoadStarted) return;

      this.frameLoadStarted = true;
      this.frameCache = this.frameUrls.map((url) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        return image;
      });

      const batchSize = 9;

      for (let start = 0; start < this.frameCache.length; start += batchSize) {
        const batch = this.frameCache.slice(start, start + batchSize);
        await Promise.all(batch.map((image) => this.waitForFrame(image)));
      }

      if (!this.isConnected) return;

      this.framesReady = true;
      this.setupScroll();
    }

    async waitForFrame(image) {
      if (!image.complete) {
        await new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }

      if (
        image.naturalWidth > 0 &&
        window.matchMedia('(min-width: 1025px)').matches &&
        typeof image.decode === 'function'
      ) {
        try {
          await image.decode();
        } catch (error) {
          // A loaded frame can still render if a browser declines explicit decoding.
        }
      }
    }

    setupScroll() {
      const firstFrame = this.frameCache.find((image) => image.naturalWidth > 0);

      if (firstFrame) {
        this.style.setProperty(
          '--wallet-scroll-media-aspect',
          `${firstFrame.naturalWidth} / ${firstFrame.naturalHeight}`
        );
      }

      this.classList.add('is-ready');
      this.updateProgress(0);

      this.scrollTrigger?.kill();
      this.removeNativeScrollFallback();
      this.classList.add('is-scroll-active');
      this.updateScrollLayout();

      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        this.setupNativeScrollFallback();
        return;
      }

      window.gsap.registerPlugin(window.ScrollTrigger);

      this.scrollTrigger = window.ScrollTrigger.create({
        trigger: this,
        start: () => `top top+=${this.getHeaderOffset()}`,
        end: () => `+=${this.getScrollDistance()}`,
        invalidateOnRefresh: true,
        onRefreshInit: this.updateScrollLayout,
        onUpdate: (self) => this.updateProgress(self.progress),
        onRefresh: (self) => this.updateProgress(self.progress),
        onLeave: () => this.updateProgress(1),
        onLeaveBack: () => this.updateProgress(0),
      });

      window.ScrollTrigger.refresh();
    }

    setupNativeScrollFallback() {
      if (this.nativeScrollActive) return;

      this.nativeScrollActive = true;
      window.addEventListener('scroll', this.handleNativeScroll, { passive: true });
      window.addEventListener('resize', this.handleNativeScroll);
      this.updateNativeProgress();
    }

    removeNativeScrollFallback() {
      if (!this.nativeScrollActive) return;

      this.nativeScrollActive = false;
      window.removeEventListener('scroll', this.handleNativeScroll);
      window.removeEventListener('resize', this.handleNativeScroll);

      if (this.nativeScrollFrame) {
        window.cancelAnimationFrame(this.nativeScrollFrame);
        this.nativeScrollFrame = null;
      }
    }

    handleNativeScroll() {
      if (this.nativeScrollFrame) return;

      this.nativeScrollFrame = window.requestAnimationFrame(() => {
        this.nativeScrollFrame = null;
        this.updateNativeProgress();
      });
    }

    updateNativeProgress() {
      if (!this.stage || !this.isConnected) return;

      this.updateScrollLayout();

      const headerOffset = this.getHeaderOffset();
      const sectionTop = this.getBoundingClientRect().top + window.scrollY;
      const scrollDistance = this.getScrollDistance();
      const progress = scrollDistance > 0
        ? (window.scrollY - sectionTop + headerOffset) / scrollDistance
        : 0;

      this.updateProgress(Math.min(1, Math.max(0, progress)));
    }

    updateProgress(progress) {
      this.renderFrame(progress);
      this.updateContent(progress);
    }

    renderFrame(progress) {
      if (!this.framesReady || this.frameCache.length === 0) return;

      const safeProgress = Math.min(1, Math.max(0, progress));
      const targetIndex = Math.round(safeProgress * (this.frameCache.length - 1));
      const targetFrame = this.frameCache[targetIndex];

      if (
        targetIndex === this.currentFrameIndex ||
        !targetFrame ||
        !targetFrame.complete ||
        targetFrame.naturalWidth === 0
      ) {
        return;
      }

      this.currentFrameIndex = targetIndex;
      this.frameImage.src = targetFrame.src;
    }

    updateContent(progress) {
      const safeProgress = Math.min(1, Math.max(0, progress));

      if (this.backgroundCopyInner) {
        const backgroundCopy = this.backgroundCopyInner.parentElement;
        const visibleHeight = backgroundCopy?.clientHeight || window.innerHeight;
        const contentHeight = this.backgroundCopyInner.scrollHeight;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;

        if (isMobile) {
          const overflowDistance = Math.max(0, contentHeight - visibleHeight);
          const startOffset = overflowDistance === 0 ? Math.min(80, visibleHeight * 0.18) : 0;
          const endOffset = -overflowDistance;
          const offset = startOffset + (endOffset - startOffset) * safeProgress;
          this.backgroundCopyInner.style.transform = `translate3d(0, ${offset}px, 0)`;
        } else {
          const topPadding = backgroundCopy
            ? Number.parseFloat(window.getComputedStyle(backgroundCopy).paddingTop) || 0
            : 0;
          const passageTravel = topPadding + contentHeight - visibleHeight * 0.35;
          const travel = Math.max(window.innerHeight * 0.9, passageTravel);
          this.backgroundCopyInner.style.transform = `translate3d(0, ${-safeProgress * travel}px, 0)`;
        }
      }

      if (this.captions.length === 0) return;

      const activeIndex = Math.min(
        this.captions.length - 1,
        Math.floor(safeProgress * this.captions.length)
      );

      this.captions.forEach((caption, index) => {
        const isActive = index === activeIndex;
        caption.classList.toggle('is-active', isActive);
        caption.setAttribute('aria-hidden', String(!isActive));
      });
    }

    getHeaderOffset() {
      const header = document.querySelector('[data-header-section]');
      return header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    }

    updateScrollLayout() {
      if (!this.stage) return;

      this.style.setProperty('--wallet-scroll-sticky-top', `${this.getHeaderOffset()}px`);
      this.style.setProperty('--wallet-scroll-distance', `${this.getScrollDistance()}px`);
      this.style.setProperty('--wallet-scroll-stage-height', `${this.stage.offsetHeight}px`);
    }

    getScrollDistance() {
      if (!window.matchMedia('(max-width: 767px)').matches) {
        return window.innerHeight * 4.5;
      }

      return Math.round(window.innerHeight * 2);
    }

    handleThemeEditorRefresh() {
      window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.framesReady) return;

        this.setupScroll();
      });
    }
  }

  customElements.define('wallet-scroll-scrub', WalletScrollScrub);
}
